// @flow
/* eslint react/jsx-no-bind:0 */

import React, { Component } from 'react';
import EmailPasswordStep from 'app/common/react/components/growth/unauth/signup/EmailPasswordStep/EmailPasswordStep';
import type { FacebookUserData } from 'app/packages/pinterest-flow-types/authentication';
import type { SignupCredentialsAndLoggingData } from 'app/packages/pinterest-flow-types/registration';
import { compose } from 'redux';
import { RecaptchaTokenConsumer } from 'app/www-unauth/components/App/RecaptchaToken';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';

const styles = {
  signup: {
    margin: '0 auto',
    position: 'relative',
    textAlign: 'center',
  },
  inputStyleOverrides: {
    backgroundColor: '#f0f0f0',
    border: '0px',
    outline: 'none',
  },
};

const SIGNUP_STEP = Object.freeze({
  SIGNUP: 'signup',
});

export type SignupStep = $Values<typeof SIGNUP_STEP>;

type Props = {|
  additionalEmailPasswordChildren?: React$Element<any>,
  additionalNameAgeGenderChildren?: React$Element<any>,
  age: ?string,
  container: string,
  disablePostSignupNextUrl?: boolean,
  dark?: boolean,
  email: ?string,
  emailValidationError?: ?string,
  loadingIndicatorCallback: (on: boolean) => void,
  passwordValidationError?: ?string,
  gender?: ?string,
  name?: ?string,
  nextUrlParam?: string,
  onAgeChange: (e: SyntheticInputEvent<HTMLInputElement> & { value: string }) => void,
  onEmailChange: (e: SyntheticInputEvent<HTMLInputElement> & { value: string }) => void,
  onFBEmailDeniedPerm?: (
    network: string,
    userData: FacebookUserData,
    credentialsAndLoggingData: SignupCredentialsAndLoggingData,
  ) => void,
  onSocialConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: SignupCredentialsAndLoggingData,
  ) => void,
  onLoginWithMfa: (creds: Object, data: Object) => void,
  onGenderChange: (e: SyntheticInputEvent<HTMLInputElement> & { value: string }) => void,
  onNameChange: (e: SyntheticInputEvent<HTMLInputElement> & { value: string }) => void,
  onPasswordChange: (e: SyntheticInputEvent<HTMLInputElement> & { value: string }) => void,
  onStepChange: (step: SignupStep) => void,
  password?: ?string,
  seoUnauthExperiments: UnauthSeoExperiments,
  setNameFromEmail?: () => void,
  showLoginWelcomeLoadingFn?: (boolean) => void,
  showSignupWelcomeLoadingFn?: (boolean) => void,
  showResponseMessageModal?: (isSignup: boolean) => void,
  signupSeparatorContinuous?: boolean,
  signupSource?: string,
  sm?: ?string,
  suppressFocus?: boolean,
  clearPassword?: () => void,
  onShowRegisterForm?: () => void,
  setWelcomeBackMessageForExistingEmail?: (shouldSet: boolean) => void,
  onFBPhoneOnlyConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: Object,
  ) => void,
|};

type State = {|
  step: SignupStep,
|};

export class Signup extends Component<Props, State> {
  static defaultProps = {
    dark: true,
  };
  state = {
    step: SIGNUP_STEP.SIGNUP,
  };

  showStep(step: SignupStep): ?React$Element<any> {
    if (step === SIGNUP_STEP.SIGNUP) {
      return (
        <RecaptchaTokenConsumer>
          {(recaptchaTokenV3) => (
            <EmailPasswordStep
              additionalChildren={this.props.additionalEmailPasswordChildren}
              age={this.props.age}
              container={this.props.container}
              dark={this.props.dark}
              disablePostSignupNextUrl={this.props.disablePostSignupNextUrl}
              email={this.props.email}
              emailValidationError={this.props.emailValidationError}
              setWelcomeBackMessageForExistingEmail={
                this.props.setWelcomeBackMessageForExistingEmail
              }
              loadingIndicatorCallback={this.props.loadingIndicatorCallback}
              name={this.props.name}
              nextUrlParam={this.props.nextUrlParam}
              passwordValidationError={this.props.passwordValidationError}
              inputStyleOverrides={styles.inputStyleOverrides}
              onAgeChange={this.props.onAgeChange}
              onFBPhoneOnlyConnectVerified={this.props.onFBPhoneOnlyConnectVerified}
              onEmailChange={this.props.onEmailChange}
              onFBEmailDeniedPerm={this.props.onFBEmailDeniedPerm}
              onSocialConnectVerified={this.props.onSocialConnectVerified}
              onLoginWithMfa={this.props.onLoginWithMfa}
              onPasswordChange={this.props.onPasswordChange}
              password={this.props.password}
              setNameFromEmail={this.props.setNameFromEmail}
              showLoginWelcomeLoadingFn={this.props.showLoginWelcomeLoadingFn}
              showSignupWelcomeLoadingFn={this.props.showSignupWelcomeLoadingFn}
              signupSeparatorContinuous={this.props.signupSeparatorContinuous}
              signupSource={this.props.signupSource}
              sm={this.props.sm}
              suppressFocus={this.props.suppressFocus}
              clearPassword={this.props.clearPassword}
              onShowRegisterForm={this.props.onShowRegisterForm}
              showResponseMessageModal={this.props.showResponseMessageModal}
              recaptchaTokenV3={recaptchaTokenV3}
            />
          )}
        </RecaptchaTokenConsumer>
      );
    }
    return undefined;
  }

  render() {
    return (
      <div data-test-id="signup" style={styles.signup}>
        {this.showStep(this.state.step)}
      </div>
    );
  }
}

export default compose(withUnauthSeoExperiments)(Signup);
